// JobCandidates.jsx
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  UsersIcon, BriefcaseIcon, ChevronLeftIcon, DownloadIcon,
  SearchIcon, XIcon, CheckIcon, ClockIcon, UserCheckIcon,
  ThumbsUpIcon, AwardIcon, ThumbsDownIcon, FileTextIcon,
  MapPin, Calendar, DollarSign, Building, Clock, Users
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  DragDropContext,
  Droppable,
  Draggable
} from "@hello-pangea/dnd";
import { API_BASE_URL } from "../config/apiconfig";
import StatusChangeModal from "../components/StatusChangeModal";
import ScheduleEventModal from "../components/ScheduleEventModal";


const statusConfig = {
  Screening:           { icon: <FileTextIcon className="w-4 h-4" />,  color: "bg-gray-100 text-gray-800" },
  Qualified:           { icon: <CheckIcon className="w-4 h-4" />,     color: "bg-blue-100 text-blue-800" },
  "Screening Disqualified": { icon: <XIcon className="w-4 h-4" />,    color: "bg-red-100 text-red-800" },
  Interviews:          { icon: <ClockIcon className="w-4 h-4" />,     color: "bg-purple-100 text-purple-800" },
  Shortlisted:         { icon: <UserCheckIcon className="w-4 h-4" />, color: "bg-yellow-100 text-yellow-800" },
  Offer:               { icon: <ThumbsUpIcon className="w-4 h-4" />,  color: "bg-green-100 text-green-800" },
  Hired:               { icon: <AwardIcon className="w-4 h-4" />,     color: "bg-green-500 text-white" },
  "Offer Declined":    { icon: <ThumbsDownIcon className="w-4 h-4" />,color: "bg-orange-100 text-orange-800" },
  Rejected:            { icon: <XIcon className="w-4 h-4" />,         color: "bg-red-500 text-white" }
};

const allStatuses = Object.keys(statusConfig);

const JobDetailsCard = ({ jobDetails }) => {
  if (!jobDetails) return null;

  const formatSalary = () => {
    if (jobDetails.include_salary && jobDetails.minimum_salary && jobDetails.maximum_salary) {
      return `${jobDetails.currency} ${jobDetails.minimum_salary.toLocaleString()} - ${jobDetails.maximum_salary.toLocaleString()}/${jobDetails.paid_every?.toLowerCase() || 'month'}`;
    }
    return jobDetails.salary || 'Not specified';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">
            {jobDetails.title}
          </h2>
          <div className="flex items-center text-sm text-gray-600 mb-2">
            <span className="capitalize">{jobDetails.classification}</span>
            <span className="mx-2">•</span>
            <span>{jobDetails.employment_type}</span>
            <span className="mx-2">•</span>
            <span>{jobDetails.location}</span>
            <span className="mx-2">•</span>
            <span>{formatSalary()}</span>
            <span className="mx-2">•</span>
            <span>Posted on {formatDate(jobDetails.post_date)}</span>
          </div>
        </div>
        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(jobDetails.status)}`}>
          {jobDetails.status}
        </span>
      </div>

      {jobDetails.skills && (
        <div className="flex flex-wrap gap-2 mb-4">
          {jobDetails.skills.split(',').map((skill, index) => (
            <span 
              key={index}
              className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded"
            >
              {skill.trim()}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t">
        <div className="flex items-center space-x-3">
          <Building className="w-5 h-5 text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-900">Department</p>
            <p className="text-sm text-gray-600">{jobDetails.department}</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Calendar className="w-5 h-5 text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-900">Closing Date</p>
            <p className="text-sm text-gray-600">{formatDate(jobDetails.closing_date)}</p>
          </div>
        </div>

        {jobDetails.company_name && (
          <div className="flex items-center space-x-3">
            <Building className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Company</p>
              <p className="text-sm text-gray-600">{jobDetails.company_name}</p>
            </div>
          </div>
        )}

        {jobDetails.recruiter_name && (
          <div className="flex items-center space-x-3">
            <Users className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Recruiter</p>
              <p className="text-sm text-gray-600">{jobDetails.recruiter_name}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function JobCandidates() {
  const { id: jobId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  console.log('JobCandidates - Raw URL params:', useParams());
  console.log('JobCandidates - Extracted jobId:', jobId);
  console.log('JobCandidates - Current URL:', window.location.pathname);

  const [columns, setColumns] = useState(() => (
    Object.fromEntries(allStatuses.map(s => [s, []]))
  ));
  const [loading, setLoading] = useState(true);
  const [jobDetails, setJobDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // Modal state for template selection on status change
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalAppId, setStatusModalAppId] = useState(null);
  const [statusModalNewStatus, setStatusModalNewStatus] = useState("");
  const [prevColumnsSnapshot, setPrevColumnsSnapshot] = useState(null);
  const [pendingMove, setPendingMove] = useState(null); // { appEmail, newStatus }
  // Schedule modal state (for Interviews/Scheduled moves)
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAppId, setScheduleAppId] = useState(null);
  const [scheduleRecruiterId, setScheduleRecruiterId] = useState(null);
  const [scheduleCandidateEmail, setScheduleCandidateEmail] = useState("");

  
// Helper to refresh job + applications and rebuild columns from server
const refreshApplications = async () => {
  try {
    setLoading(true);
    const jobRes = await fetch(`${API_BASE_URL}/api/job-postings/${jobId}`);
    setJobDetails(await jobRes.json());

    const appRes = await fetch(`${API_BASE_URL}/api/applications/job/${jobId}`);
    const raw = await appRes.json();

    // Initialize empty columns each refresh
    const blank = Object.fromEntries(allStatuses.map(s => [s, []]));
    raw.forEach(app => {
      const cleaned = {
        ...app,
        status: app.status || "Screening",
        applicant_name: app.applicant_name || app.applicant_email?.split('@')[0] || `User ${app.applicant_id}`,
        applicant_email: app.applicant_email || "No email",
        resume_name: app.resume_name || null
      };
      if (!blank[cleaned.status]) blank[cleaned.status] = [];
      blank[cleaned.status].push(cleaned);
    });
    setColumns(blank);
  } catch (e) {
    console.error(e);
  } finally {
    setLoading(false);
  }
};

// Fixed useEffect in JobCandidates.jsx
useEffect(() => {
  refreshApplications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [jobId]);

  const updateStatusOnServer = async (applicationId, newStatus) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/applications/${applicationId}/status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus })
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (err) {
      console.error("Status update failed", err);
      throw err;
    }
  };

  const createEmployeeFromCandidate = async (applicantEmail) => {
  try {
    // Confirm with the user before creating an employee
    const shouldCreate = window.confirm(
      'This will create a new employee record in the HRMS system. Continue?'
    );
    
    if (!shouldCreate) return false;

    // 1. First fetch candidate details from profile table
    const candidateRes = await fetch(`${API_BASE_URL}/api/profile/by-email/${encodeURIComponent(applicantEmail)}`);
    
    if (!candidateRes.ok) {
      throw new Error(`Failed to fetch candidate profile: ${candidateRes.status}`);
    }
    
    const candidate = await candidateRes.json();
    
    if (!candidate) {
      throw new Error('Candidate not found in database');
    }

    // 2. Get recruiter's company ID
    const recruiterRes = await fetch(`${API_BASE_URL}/api/recruiter/profile/${user.id}`);
    
    if (!recruiterRes.ok) {
      throw new Error(`Failed to fetch recruiter profile: ${recruiterRes.status}`);
    }
    
    const recruiterData = await recruiterRes.json();
    const companyId = recruiterData.company_id;

    if (!companyId) {
      throw new Error('Recruiter company not found');
    }

    // 3. Try to find matching department in VHRMS
    let departmentId = null;
    const departmentName = jobDetails?.department || '';
    
    if (departmentName) {
      try {
        const departmentsRes = await fetch(`${API_BASE_URL}/api/vhrms/departments`);
        if (departmentsRes.ok) {
          const departments = await departmentsRes.json();
          const department = departments.find(dept => 
            dept.dname && dept.dname.toLowerCase() === departmentName.toLowerCase()
          );
          
          if (department) {
            departmentId = department.id;
            console.log(`Found matching department: ${department.dname} (ID: ${department.id})`);
          }
        }
      } catch (error) {
        console.log('Failed to fetch departments, using NULL for department:', error);
      }
    }

    // 4. Prepare employee data with proper name construction
    const emptyToNull = (value) => (value && value.trim() !== '') ? value : null;
    
    // Construct full name from firstName and lastName
    const firstName = candidate.firstName || '';
    const lastName = candidate.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim() || candidate.username || applicantEmail.split('@')[0] || 'New Employee';
    
    const employeeData = {
      email: candidate.email,
      password: '123456',
      empNo: `EMP-${Date.now().toString().slice(-6)}`,
      name: fullName, // Use constructed full name
      mobile: candidate.phoneCode && candidate.phoneNumber 
        ? `${candidate.phoneCode}${candidate.phoneNumber}`
        : candidate.mobile || '',
      department: departmentId,
      designation: jobDetails?.title || 'New Employee',
      joinDate: new Date().toISOString().split('T')[0],
      salary: jobDetails?.minimum_salary || 0,
      address: candidate.location || candidate.address || '',
      gender: candidate.gender || '', 
      maritalStatus: candidate.maritalStatus || '', 
      bank: candidate.bank || '', 
      bankAccNo: emptyToNull(candidate.bankAccNo),
      birthday: emptyToNull(candidate.birthday),
      nicNumber: emptyToNull(candidate.nicNumber),
      userType: 'employee',
      company_id: companyId
    };

    // 5. Create employee in VHRMS database
    const createRes = await fetch(`${API_BASE_URL}/api/vhrms/employees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(employeeData)
    });

    if (!createRes.ok) {
      const errorData = await createRes.json();
      throw new Error(errorData.error || 'Failed to create employee');
    }

    const result = await createRes.json();
    console.log('Employee created successfully:', result);
    alert(`Employee "${fullName}" created successfully in HRMS system`);
    return true;
  } catch (error) {
    console.error('Error creating employee:', error);
    alert('Failed to create employee: ' + error.message);
    return false;
  }
};

  const onDragEnd = async ({ source, destination, draggableId }) => {
    if (!destination) return;

    const start = source.droppableId;
    const finish = destination.droppableId;
    const startIdx = source.index;
    const finishIdx = destination.index;

    const startList = Array.from(columns[start]);
    const [moved] = startList.splice(startIdx, 1);

    if (start === finish) {
      startList.splice(finishIdx, 0, moved);
      setColumns({ ...columns, [start]: startList });
      return;
    }

    // For Interview/Scheduled, use full scheduling flow (same modal as Candidate Details)
    if (finish === 'Interviews' || finish === 'Scheduled') {
      const snapshot = structuredClone(columns);
      setPrevColumnsSnapshot(snapshot);

      const finishList = Array.from(columns[finish]);
      finishList.splice(finishIdx, 0, { ...moved, status: finish });
      setColumns({
        ...columns,
        [start]: startList,
        [finish]: finishList
      });

      setScheduleAppId(draggableId);
      setScheduleRecruiterId(user?.id);
      setScheduleCandidateEmail(moved.applicant_email);
      setScheduleOpen(true);
      return;
    }

    const statusesRequiringTemplates = new Set(['Offer','Hired','Rejected','Screening Disqualified']);

    if (statusesRequiringTemplates.has(finish)) {
      // Open modal for template selection. Do optimistic UI but keep snapshot to revert on cancel.
      const snapshot = structuredClone(columns);
      setPrevColumnsSnapshot(snapshot);

      const finishList = Array.from(columns[finish]);
      finishList.splice(finishIdx, 0, { ...moved, status: finish });
      setColumns({
        ...columns,
        [start]: startList,
        [finish]: finishList
      });

      setPendingMove({ appEmail: moved.applicant_email, newStatus: finish });
      setStatusModalAppId(draggableId);
      setStatusModalNewStatus(finish);
      setStatusModalOpen(true);
      return;
    }

    // Regular statuses: optimistic update with revert on failure
    const snapshot = structuredClone(columns);
    const finishList = Array.from(columns[finish]);
    finishList.splice(finishIdx, 0, { ...moved, status: finish });
    setColumns({
      ...columns,
      [start]: startList,
      [finish]: finishList
    });

    try {
      await updateStatusOnServer(draggableId, finish);
      if (finish === 'Hired') {
        await createEmployeeFromCandidate(moved.applicant_email);
      }
    } catch (error) {
      console.error("Operation failed, reverting UI", error);
      setColumns(snapshot);
    }
  };

  const visibleColumns = React.useMemo(() => {
    if (!searchTerm) return columns;
    const lc = searchTerm.toLowerCase();
    return Object.fromEntries(
      allStatuses.map(status => [
        status,
        columns[status].filter(
          a =>
            a.applicant_name.toLowerCase().includes(lc) ||
            a.applicant_email.toLowerCase().includes(lc)
        )
      ])
    );
  }, [columns, searchTerm]);

  const CandidateCard = ({ app, index }) => (
    <Draggable draggableId={String(app.id)} index={index}>
      {(drag, snapshot) => (
        <div
          ref={drag.innerRef} {...drag.draggableProps} {...drag.dragHandleProps}
          className={`bg-white p-3 rounded-lg shadow-sm border transition
            ${snapshot.isDragging ? "shadow-lg rotate-3 scale-105" : "hover:shadow-md"}`}
        >
          <button
            className="font-medium text-blue-600 hover:underline"
            onClick={e => {
              e.stopPropagation();
              navigate(`/candidate-details/${encodeURIComponent(app.applicant_email)}`);
            }}
          >
            {app.applicant_name}
          </button>
          <div className="text-sm text-gray-500 truncate">{app.applicant_email}</div>
          <div className="text-xs text-gray-400 mt-1">
            Applied: {new Date(app.application_date).toLocaleDateString()}
          </div>
          {app.resume_name && (
            <div className="mt-2 flex items-center text-xs text-blue-600">
              <DownloadIcon className="w-3 h-3 mr-1" />Resume
            </div>
          )}
        </div>
      )}
    </Draggable>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />

      <div className="flex-1 p-6 max-w-full mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/recruiter-dashboard" className="flex items-center text-blue-600 hover:text-blue-800">
              <ChevronLeftIcon className="w-5 h-5 mr-1" />Back to Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">
              Hiring Pipeline
            </h1>
          </div>
        </div>

        <JobDetailsCard jobDetails={jobDetails} />

        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-64">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text" placeholder="Search candidates…"
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <span className="text-sm text-gray-500">
              {Object.values(columns).flat().length} candidate{Object.values(columns).flat().length !== 1 && "s"}
            </span>
          </div>

          {loading ? (
            <div className="p-8 text-center">Loading candidates…</div>
          ) : Object.values(columns).flat().length === 0 ? (
            <EmptyState term={searchTerm} />
          ) : (
            <div className="overflow-x-auto">
              <DragDropContext onDragEnd={onDragEnd}>
                <div className="flex p-4 space-x-4" style={{ minWidth: `${allStatuses.length * 288}px` }}>
                  {allStatuses.map(status => (
                    <Droppable key={status} droppableId={status}>
                      {(drop, snap) => (
                        <div
                          ref={drop.innerRef} {...drop.droppableProps}
                          className={`flex-shrink-0 w-72 rounded-lg p-3 min-h-[200px]
                            ${snap.isDraggingOver ? "bg-gray-50 border-2 border-blue-200" : "bg-gray-50"}`}
                        >
                          <ColumnHeader status={status} count={visibleColumns[status].length} />
                          <div className="space-y-3">
                            {visibleColumns[status].map((app, i) => (
                              <CandidateCard key={app.id} app={app} index={i} />
                            ))}
                            {drop.placeholder}
                          </div>
                        </div>
                      )}
                    </Droppable>
                  ))}
                </div>
              </DragDropContext>
            </div>
          )}
        </div>
      </div>

      <Footer />
      {/* Status Change Modal for template selection */}
      <StatusChangeModal
        visible={statusModalOpen}
        onCancel={() => {
          setStatusModalOpen(false);
          if (prevColumnsSnapshot) setColumns(prevColumnsSnapshot);
          setPrevColumnsSnapshot(null);
          setPendingMove(null);
        }}
        applicationId={statusModalAppId}
        recruiterId={user?.id}
        status={statusModalNewStatus}
        onSubmitted={async () => {
          setStatusModalOpen(false);
          setPrevColumnsSnapshot(null);
          if (pendingMove?.newStatus === 'Hired' && pendingMove?.appEmail) {
            await createEmployeeFromCandidate(pendingMove.appEmail);
          }
          setPendingMove(null);
          // Ensure realtime UI is in sync with server state
          await refreshApplications();
        }}
      />

      {/* Schedule Event Modal for Interviews/Scheduled moves */}
      <ScheduleEventModal
        visible={scheduleOpen}
        onCancel={async (success) => {
          // onCancel(true) means success (per modal's implementation)
          setScheduleOpen(false);
          if (success) {
            await refreshApplications();
          } else if (prevColumnsSnapshot) {
            setColumns(prevColumnsSnapshot);
          }
          setPrevColumnsSnapshot(null);
          setScheduleAppId(null);
          setScheduleRecruiterId(null);
          setScheduleCandidateEmail("");
        }}
        applicationId={scheduleAppId}
        recruiterId={scheduleRecruiterId}
        candidateEmail={scheduleCandidateEmail}
      />
    </div>
  );
}

const Meta = ({ label, value }) => (
  <div>
    <p className="text-sm text-gray-500">{label}</p>
    <p className="font-medium">{value}</p>
  </div>
);

const EmptyState = ({ term }) => (
  <div className="p-8 text-center">
    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
      <UsersIcon className="w-8 h-8 text-gray-400" />
    </div>
    <p className="text-gray-500">
      {term ? "No candidates match your search" : "No applications for this job yet"}
    </p>
  </div>
);

const ColumnHeader = ({ status, count }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center">
      <span className={`w-5 h-5 rounded-full flex items-center justify-center mr-2 ${statusConfig[status].color}`}>
        {statusConfig[status].icon}
      </span>
      <h3 className="font-medium">{status}</h3>
    </div>
    <span className="text-sm text-gray-500">{count}</span>
  </div>
);